import React from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { PeriodPreset } from "../../hooks/usePeriodReportBuilder";

interface PeriodSelectorProps {
  selectedPeriod: PeriodPreset;
  onPeriodChange: (period: PeriodPreset) => void;
  customStartDate: Date | null;
  customEndDate: Date | null;
  onCustomStartDateChange: (date: Date | null) => void;
  onCustomEndDateChange: (date: Date | null) => void;
  onGenerateReport: () => void;
  canGenerate: boolean;
  loading: boolean;
}

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  onGenerateReport,
  canGenerate,
  loading,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-2 rounded-lg">
        <Button
          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
            ${
              selectedPeriod === "last_30_days"
                ? "bg-white shadow text-black hover:bg-gray-50"
                : "text-gray-500 hover:bg-gray-200"
            }`}
          onClick={() => onPeriodChange("last_30_days")}
          type="button"
          variant="ghost"
        >
          Last 30 Days
        </Button>

        <Button
          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
            ${
              selectedPeriod === "current_month"
                ? "bg-white shadow text-black hover:bg-gray-50"
                : "text-gray-500 hover:bg-gray-200"
            }`}
          onClick={() => onPeriodChange("current_month")}
          type="button"
          variant="ghost"
        >
          Current Month
        </Button>

        <Button
          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
            ${
              selectedPeriod === "last_month"
                ? "bg-white shadow text-black hover:bg-gray-50"
                : "text-gray-500 hover:bg-gray-200"
            }`}
          onClick={() => onPeriodChange("last_month")}
          type="button"
          variant="ghost"
        >
          Last Month
        </Button>

        <Button
          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
            ${
              selectedPeriod === "current_year"
                ? "bg-white shadow text-black hover:bg-gray-50"
                : "text-gray-500 hover:bg-gray-200"
            }`}
          onClick={() => onPeriodChange("current_year")}
          type="button"
          variant="ghost"
        >
          Current Year
        </Button>

        <Button
          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
            ${
              selectedPeriod === "last_year"
                ? "bg-white shadow text-black hover:bg-gray-50"
                : "text-gray-500 hover:bg-gray-200"
            }`}
          onClick={() => onPeriodChange("last_year")}
          type="button"
          variant="ghost"
        >
          Last Year
        </Button>

        <Button
          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
            ${
              selectedPeriod === "custom"
                ? "bg-white shadow text-black hover:bg-gray-50"
                : "text-gray-500 hover:bg-gray-200"
            }`}
          onClick={() => onPeriodChange("custom")}
          type="button"
          variant="ghost"
        >
          Custom
        </Button>

        <Button
          className="ml-auto"
          disabled={!canGenerate || loading}
          onClick={onGenerateReport}
          type="button"
        >
          {loading ? "Generating..." : "Generate Report"}
        </Button>
      </div>

      {selectedPeriod === "custom" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <DatePicker
              onChange={onCustomStartDateChange}
              placeholder="Select start date"
              value={customStartDate}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <DatePicker
              minDate={customStartDate}
              onChange={onCustomEndDateChange}
              placeholder="Select end date"
              value={customEndDate}
            />
          </div>
        </div>
      )}
    </div>
  );
};
